import { apiClient, APIResponse, PaginatedResponse, QueryOptions } from './client';

export interface Component {
  id: number;
  scanid: number;
  applicationid?: number;
  projectid?: number;
  name: string;
  applicationName?: string;
  projectName?: string;
  scanVersion?: string;
  version?: string;
  referenceid?: string;
  homepageurl?: string;
  licensename?: string;
  licensemainid?: number;
  license_spdx_identifier?: string;
  licenseurl?: string;
  description?: string;
  vulnerabilities?: any;
  createdon: string;
  workgroupid: number;

  overallscore?: number;
  vulnerabilityscore?: number;
  licensescore?: number;
  operationalriskscore?: number;
  technicaldebtscore?: number;
  businessriskscore?: number;
  cvssscore?: number;
  epssscore?: number;
  isexploitavailable?: boolean;
  isfixavailable?: boolean;
  isencrypted?: boolean;
  isdeprecated?: boolean;
  isoutdated?: boolean;

  repoid?: number;
  repousername?: string;
  repoprovider?: string;
  repourl?: string;
  reponame?: string;
  repotype?: string;
  binaryartifacturl?: string;
  sourceartifacturl?: string;
  dayssincelastcommit?: number;
  githubstars?: number | null;
  githubforks?: number | null;
  githubcontributors?: number | null;
  maintenance_status?: 'Active' | 'Recent' | 'Poor' | 'Abandoned';

  declaredlocations?: string;
  curatedlocations?: string;
  has_declared_locations?: boolean;
  has_curated_locations?: boolean;
  has_binary_artifact?: boolean;
  has_source_artifact?: boolean;

  total_vulnerabilities?: number;
  critical_vulnerabilities?: number;
  high_vulnerabilities?: number;
  medium_vulnerabilities?: number;
  low_vulnerabilities?: number;
  max_cvss_score?: number;
  min_cvss_score?: number;
  avg_cvss_score?: number;
  ghsa_vulnerabilities?: number;
  exploit_db_available_count?: number;
  fix_available_count?: number;
  vulnerabilities_with_references?: number;
  vulnerabilities_with_remediation?: number;
  unique_cvss_versions?: number;

  avg_epss_score?: number;
  max_epss_score?: number;
  min_epss_score?: number;
  highest_epss_severity?: string;
  high_epss_vulnerabilities?: number;
  medium_epss_vulnerabilities?: number;
  low_epss_vulnerabilities?: number;

  total_license_issues?: number;
  critical_license_issues?: number;
  high_license_issues?: number;
  medium_license_issues?: number;
  low_license_issues?: number;
  unique_license_types?: number;
  unique_issue_types?: number;
  license_issues_with_recommendations?: number;
  primary_license_issue_type?: string;
  license_risk_level?: 'Critical' | 'High' | 'Medium' | 'Low';
  detected_licenses_count?: number;
  avg_license_compliance_score?: number;

  scan_business_risk_score?: number;
  scan_operational_risk_score?: number;
  scan_technical_debt_score?: number;
  scan_overall_score?: number;
  filesscanned?: number;
  projectsize?: number;
  scanparameters?: any;
  custompolicystandards?: any;
  scan_type?: string;

  days_since_first_discovery?: number;
  days_since_last_discovery?: number;
  usage_frequency_score?: number;

  organisation_industry?: string;
  organisation_company_size?: string;
  scan_creator_department?: string;
  scan_creator_designation?: string;

  risk_score?: number;
  risk_level?: 'Critical' | 'High' | 'Medium' | 'Low';
  compliance_score?: number;
  security_maturity_score?: number;
}

export interface ComponentsMetrics {
  totalComponents: number;
  uniqueComponents: number;
  vulnerableComponents: number;
  outdatedComponents: number;
  deprecatedComponents: number;
  averageVulnerabilityScore: number;
  averageLicenseScore: number;
  averageOverallScore: number;
  riskDistribution: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  licenseDistribution: Record<string, number>;
  topVulnerableComponents: Component[];
}

export interface FindComponentsOptions extends QueryOptions {
  projectIds?: number[];
  applicationIds?: number[];
}


export class ComponentsAPI {
  static async getAll(options?: FindComponentsOptions): Promise<APIResponse<PaginatedResponse<Component>>> {
    return apiClient.get<PaginatedResponse<Component>>('/scans/components', options);
  }


  static async getById(id: number): Promise<APIResponse<Component>> {
    return apiClient.get<Component>(`/components/${id}`);
  }

  static async getByWorkgroup(workgroupId: number, options?: QueryOptions): Promise<APIResponse<Component[]>> {
    const params = new URLSearchParams();
    params.append('workgroupId', workgroupId.toString());

    if (options?.searchText) params.append('searchText', options.searchText);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.perPage) params.append('perPage', options.perPage.toString());

    return apiClient.get<Component[]>(`/components/workgroup/${workgroupId}?${params.toString()}`);
  }

  static async getByScan(scanId: number, options?: QueryOptions): Promise<APIResponse<Component[]>> {
    const params = new URLSearchParams();

    if (options?.searchText) params.append('searchText', options.searchText);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.perPage) params.append('perPage', options.perPage.toString());

    return apiClient.get<Component[]>(`/components/scan/${scanId}?${params.toString()}`);
  }

  static async getByProject(projectId: number, options?: QueryOptions): Promise<APIResponse<Component[]>> {
    const params = new URLSearchParams();

    if (options?.searchText) params.append('searchText', options.searchText);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.perPage) params.append('perPage', options.perPage.toString());

    return apiClient.get<Component[]>(`/components/project/${projectId}?${params.toString()}`);
  }

  static async getByApplication(applicationId: number, options?: QueryOptions): Promise<APIResponse<Component[]>> {
    const params = new URLSearchParams();

    if (options?.searchText) params.append('searchText', options.searchText);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.perPage) params.append('perPage', options.perPage.toString());

    return apiClient.get<Component[]>(`/components/application/${applicationId}?${params.toString()}`);
  }

  static async getVulnerable(workgroupId: number, options?: QueryOptions): Promise<APIResponse<Component[]>> {
    const params = new URLSearchParams();
    params.append('workgroupId', workgroupId.toString());
    params.append('isVulnerable', 'true');

    if (options?.searchText) params.append('searchText', options.searchText);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.perPage) params.append('perPage', options.perPage.toString());

    return apiClient.get<Component[]>(`/components?${params.toString()}`);
  }

  static async getOutdated(workgroupId: number, options?: QueryOptions): Promise<APIResponse<Component[]>> {
    const params = new URLSearchParams();
    params.append('workgroupId', workgroupId.toString());
    params.append('isOutdated', 'true');

    if (options?.searchText) params.append('searchText', options.searchText);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.perPage) params.append('perPage', options.perPage.toString());

    return apiClient.get<Component[]>(`/components?${params.toString()}`);
  }

  static async getDeprecated(workgroupId: number, options?: QueryOptions): Promise<APIResponse<Component[]>> {
    const params = new URLSearchParams();
    params.append('workgroupId', workgroupId.toString());
    params.append('isDeprecated', 'true');

    if (options?.searchText) params.append('searchText', options.searchText);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.perPage) params.append('perPage', options.perPage.toString());

    return apiClient.get<Component[]>(`/components?${params.toString()}`);
  }
};
